import { useState, useEffect } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import {
  Bell,
  Briefcase,
  Building2,
  Construction,
  FileText,
  Gift,
  History,
  LayoutDashboard,
  Link2,
  Scale,
  Settings,
  ShieldCheck,
  TrendingUp,
  UserCheck,
  Users,
  Wallet,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { changelogTextColor, getChangelogIcon, type ChangelogEntry } from '@/features/admin/lib/changelog'
import { cn } from '@/lib/utils'
import { env } from '@/lib/env'
import type { TranslatedRoute } from '../app/useNavigation'
import type { NavigationIconKey } from '../app/navigation'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { AgentOnboardingCard, OwnerOnboardingCard } from '@/features/onboarding/components/OnboardingSidebarCard'

interface AppSidebarProps {
  collapsed: boolean
  navItems: TranslatedRoute[]
  mode: 'drawer' | 'collapsed-desktop' | 'normal-desktop'
  onNavigate?: () => void
  iacrmConfigured?: boolean
  navBadges?: Partial<Record<string, string>>
  isBusinessOwner?: boolean
  isAgent?: boolean
}

const routeIcons: Record<NavigationIconKey, React.ComponentType<{ className?: string }>> = {
  dashboard: LayoutDashboard,
  businesses: Building2,
  programs: Briefcase,
  'exchange-packs': Gift,
  agents: UserCheck,
  prospects: Users,
  transactions: TrendingUp,
  points: Wallet,
  exchanges: FileText,
  notifications: Bell,
  settings: Settings,
  iacrm: Link2,
  legal: Scale,
  admin: ShieldCheck,
  changelog: History,
}

export function AppSidebar({
  collapsed,
  navItems,
  mode,
  onNavigate,
  iacrmConfigured = true,
  navBadges,
  isBusinessOwner = false,
  isAgent = false,
}: AppSidebarProps) {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const isMobile = mode === 'drawer'
  // In drawer mode, sidebar is always visually expanded (w-64 with labels)
  const visuallyCollapsed = isMobile ? false : collapsed

  const [liveVersion, setLiveVersion] = useState<string>(env.appVersion)
  const [latestChange, setLatestChange] = useState<Pick<ChangelogEntry, 'type' | 'color'> | null>(null)

  useEffect(() => {
    fetch('/version.json')
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch version')
        return res.json()
      })
      .then((data) => {
        if (data?.version) setLiveVersion(data.version)
      })
      .catch(() => {
        // Fallback to env.appVersion already set in state
      })
  }, [])

  useEffect(() => {
    const lang = i18n.language === 'fr' ? 'fr' : 'en'
    fetch(`/changelog-${lang}.json?v=${Date.now()}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        const entry = data?.entries?.[0]
        if (entry) {
          setLatestChange({ type: entry.type, color: entry.color })
          if (entry.version) setLiveVersion(entry.version)
        }
      })
      .catch(() => {})
  }, [i18n.language])

  return (
    <aside
      className={[
        'flex h-screen flex-col text-foreground transition-[width] duration-300',
        'bg-background',
        // Soft divider like shadcn docs (no harsh border).
        "after:pointer-events-none after:absolute after:inset-y-0 after:right-0 after:w-px after:bg-gradient-to-b after:from-transparent after:via-border/70 after:to-transparent",
        isMobile
          ? 'w-64 shrink-0'
          : ['relative z-40', collapsed ? 'w-16' : 'w-64', 'sticky top-0 shrink-0'].join(' '),
      ].join(' ')}
    >
      <div className="flex h-16 items-center px-3">
        {visuallyCollapsed ? (
          <div className="flex w-full items-center justify-center">
            <img
              src="/Uploads/logo-mark-light.svg"
              alt="Myhd mark"
              className="h-9 w-9 dark:hidden"
            />
            <img
              src="/Uploads/logo-mark-dark.svg"
              alt="Myhd mark"
              className="hidden h-9 w-9 dark:block"
            />
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <img src="/Uploads/logo-light.svg" alt="Myhd" className="h-9 w-auto dark:hidden" />
            <img src="/Uploads/logo-dark.svg" alt="Myhd" className="hidden h-9 w-auto dark:block" />
            <span className="rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
              v{liveVersion}
            </span>
          </div>
        )}
      </div>

      <TooltipProvider delayDuration={100}>
        <nav className="flex-1 overflow-y-auto px-2 py-3">
          <ul className="space-y-1">
            {navItems.map((item) => {
              const Icon = routeIcons[item.icon]
              const isIacrmLocked = item.path === '/iacrm' && !iacrmConfigured
              const isIacrmBeta = item.path === '/iacrm'
              const navBadge = navBadges?.[item.path]
              const isChangelog = item.path === '/changelog'
              const ChangeIcon = isChangelog && latestChange ? getChangelogIcon(latestChange.type) : null
              const changeIconColor = isChangelog && latestChange ? changelogTextColor(latestChange.color) : ''

              // Pre-compute active state to avoid function className conflicting with Radix Slot
              const isActive =
                location.pathname === item.path ||
                location.pathname.startsWith(`${item.path}/`)
              const linkClassName = [
                'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors',
                isActive
                  ? 'bg-accent text-accent-foreground'
                  : 'text-muted-foreground hover:bg-accent/60 hover:text-foreground',
                visuallyCollapsed ? 'justify-center px-2' : '',
              ].join(' ')

              if (isIacrmLocked) {
                return (
                  <li key={item.path}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          onClick={() => navigate('/settings?tab=api')}
                          className={[
                            'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors',
                            'text-muted-foreground hover:bg-accent/60 hover:text-foreground',
                            visuallyCollapsed ? 'justify-center px-2' : '',
                          ].join(' ')}
                        >
                          <span className="relative shrink-0">
                            <Icon className="h-4.5 w-4.5" />
                            {visuallyCollapsed && (
                              <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-amber-400 ring-1 ring-background" />
                            )}
                          </span>
                          {!visuallyCollapsed && (
                            <>
                              <span className="truncate font-medium">{item.label}</span>
                              <span className="ml-auto shrink-0 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700 dark:bg-amber-900/60 dark:text-amber-300">
                                {t('iacrm.apiNotConfigured')}
                              </span>
                            </>
                          )}
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="right">
                        {visuallyCollapsed
                          ? t('iacrm.apiNotConfigured')
                          : t('iacrm.configureTooltip')}
                      </TooltipContent>
                    </Tooltip>
                  </li>
                )
              }

              if (isIacrmBeta) {
                return (
                  <li key={item.path}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <NavLink to={item.path} onClick={onNavigate} className={linkClassName}>
                          <span className="relative shrink-0">
                            <Icon className="h-4.5 w-4.5 shrink-0" />
                            {visuallyCollapsed && (
                              <span className="absolute -right-0.5 -top-0.5 flex size-3.5 items-center justify-center rounded-full bg-amber-400 ring-1 ring-background">
                                <Construction className="size-2 text-amber-900" />
                              </span>
                            )}
                          </span>
                          {!visuallyCollapsed && (
                            <>
                              <span className="truncate font-medium">{item.label}</span>
                              <span className="ml-auto shrink-0 rounded-full border border-amber-200 bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700 dark:border-amber-800/60 dark:bg-amber-900/60 dark:text-amber-300">
                                {t('navigation.iacrmBeta')}
                              </span>
                            </>
                          )}
                        </NavLink>
                      </TooltipTrigger>
                      <TooltipContent side="right">
                        {t('navigation.iacrmBetaTooltip')}
                      </TooltipContent>
                    </Tooltip>
                  </li>
                )
              }

              return (
                <li key={item.path}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <NavLink
                        to={item.path}
                        onClick={onNavigate}
                        className={linkClassName}
                      >
                        <span className="relative shrink-0">
                          <Icon className="h-4.5 w-4.5 shrink-0" />
                          {visuallyCollapsed && navBadge ? (
                            <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-emerald-500 ring-1 ring-background" />
                          ) : null}
                          {visuallyCollapsed && isChangelog && ChangeIcon ? (
                            <span className={cn('absolute -right-1 -top-1 flex size-4 items-center justify-center', changeIconColor)}>
                              <ChangeIcon className="size-3.5" />
                            </span>
                          ) : null}
                        </span>
                        {!visuallyCollapsed ? (
                          <>
                            <span className="truncate font-medium">{item.label}</span>
                            {navBadge ? (
                              <span className="ml-auto shrink-0 rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700 dark:text-emerald-300">
                                {navBadge}
                              </span>
                            ) : null}
                            {isChangelog && ChangeIcon ? (
                              <span className={cn('ml-auto flex size-5 items-center justify-center', changeIconColor)}>
                                <ChangeIcon className="size-4" />
                              </span>
                            ) : null}
                          </>
                        ) : null}
                      </NavLink>
                    </TooltipTrigger>
                    {visuallyCollapsed && (
                      <TooltipContent side="right">
                        {navBadge ? `${item.label} (${navBadge})` : item.label}
                      </TooltipContent>
                    )}
                  </Tooltip>
                </li>
              )
            })}
          </ul>
        </nav>
      </TooltipProvider>

      {isBusinessOwner ? (
        <OwnerOnboardingCard collapsed={visuallyCollapsed} />
      ) : isAgent ? (
        <AgentOnboardingCard collapsed={visuallyCollapsed} />
      ) : null}
    </aside>
  )
}
